import { Form } from "@remix-run/react";
import clsx from "clsx";

import { Button } from "~/components/Button";
import { BlockLoader } from "~/components/Loader";
import { usePendingNavigation } from "~/hooks/useShowSpinner";
import { MeetingTypeIndicator } from "~/modules/meeting-type/MeetingType";
import type { OfficeLoaderData } from "~/routes/$enterprise+/plugin+/$sessionId.schedule.office";
import { useMeetingTypes } from "~/routes/api+/enterprises.$enterprise.meeting-types";

interface Props {
  office: ArrayElement<OfficeLoaderData["offices"]>;
}

const NAME = "officeId";

export function OfficeSelector({ office }: Props) {
  const officeId = office.id.toString();

  const meetingTypes = useMeetingTypes({ officeId });
  const { isLoading, isLoadingForValue } = usePendingNavigation(NAME, officeId);

  return (
    <Form method="post">
      <Button
        className={clsx(
          "relative",
          "gap-sm rounded border p-sm text-regular",
          "w-full",
          "focus-visible:outline-0",
          "border-gray-200",
          //TODO change the hover background to very light brand color
          "bg-white hover:bg-gray-100",
          "text-left",
        )}
        data-component="Office"
        data-external-id={office.externalId}
        data-is-virtual={office.isVirtual}
        isDisabled={isLoading}
        type="submit"
      >
        {isLoadingForValue && <BlockLoader />}
        <div className="flex flex-row justify-between" data-component="OfficeContent">
          <div className="flex flex-col gap-xs" data-component="OfficeInfo">
            {!office.isVirtual && office.location && (
              <section
                className="flex items-center gap-xs text-sm font-normal text-secondary"
                data-component="OfficeLocation"
              >
                {office.location.address}
                {typeof office.location.distance === "number" && (
                  <span
                    className={clsx(
                      `inline-flex h-fit items-center justify-center whitespace-nowrap rounded-full border px-xs text-xs subpixel-antialiased`,
                      "border-gray-300 bg-gray-200 text-gray-500",
                    )}
                    data-component="OfficeDistance"
                  >
                    {office.location.distance} km
                  </span>
                )}
              </section>
            )}
            <section className="max-w-[500px] text-base font-semibold" data-component="OfficeTitle">
              {office.title}
            </section>
          </div>
          {meetingTypes.length > 0 && (
            // TODO: check gap-xs cs 3xs in subjects
            // TODO: are meeting types actually shown for office?
            <div className="flex items-center gap-xs" data-component="OfficeMeetingTypes">
              {meetingTypes.map((meetingType) => (
                <MeetingTypeIndicator key={meetingType} meetingType={meetingType} />
              ))}
            </div>
          )}
        </div>
        <input name={NAME} type="hidden" value={officeId} />
      </Button>
    </Form>
  );
}
